import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';

interface Group {
  openId: string | null;
  /** Opens one section and closes whichever was open, keeping `anchor` where it is on screen. */
  open: (id: string | null, anchor?: HTMLElement | null) => void;
}

const ReportSectionContext = createContext<Group | null>(null);

export function useReportSectionGroup(): Group | null {
  return useContext(ReportSectionContext);
}

/**
 * One section open at a time.
 *
 * <p>A dozen sections, each a screen and a half when open, makes a report somebody scrolls through
 * rather than reads: by the fourth one they have lost where the third ended. Opening one closes
 * the last, so the page stays about the length of the thing being read and the closed rows above
 * and below stay visible as the table of contents they already are.
 *
 * <p>The cost of closing something above you is that the page jumps - the section you just opened
 * slides up under your cursor, and you have lost your place to gain a shorter page. So the
 * heading you clicked is pinned: its distance from the top of the window is measured before the
 * change and the scroll position is corrected after it, in the same frame the browser paints. The
 * clicked row does not move at all.
 */
export default function ReportSectionGroup({ children }: { children: ReactNode }) {
  const [openId, setOpenId] = useState<string | null>(null);
  const pending = useRef<{ element: HTMLElement; top: number } | null>(null);

  const open = useCallback((id: string | null, anchor?: HTMLElement | null) => {
    if (anchor) {
      pending.current = { element: anchor, top: anchor.getBoundingClientRect().top };
    }
    setOpenId(id);
    if (!anchor) {
      return;
    }
    // After React has laid the new height out, put the clicked heading back where it was.
    window.requestAnimationFrame(() => {
      const held = pending.current;
      pending.current = null;
      if (!held || !held.element.isConnected) {
        return;
      }
      const moved = held.element.getBoundingClientRect().top - held.top;
      if (Math.abs(moved) > 1) {
        window.scrollBy({ top: moved, behavior: 'instant' as ScrollBehavior });
      }
    });
  }, []);

  const value = useMemo(() => ({ openId, open }), [openId, open]);

  return <ReportSectionContext.Provider value={value}>{children}</ReportSectionContext.Provider>;
}
